/**
 * BGP-session and CPR caps for a Reflector V2 instance, read from the API's
 * `reflectorInstance.instanceLimits` (GET bgp-config).
 *
 * Two ways a reflector gets a cap, and they are not interchangeable (CTX-484):
 *
 * - **PAYG** is metered by machine size, so its cap comes from the EC2 instance size.
 * - **BYOL** is metered by what the customer bought, so its cap comes from the license
 *   size (S / M / L) and the instance size is irrelevant.
 *
 * Both land on the same numbers — a Medium license and an `*.medium` instance allow the
 * same 25 — so the numbers live in {@link INSTANCE_SIZE_LIMITS} once and the license
 * sizes reference them through {@link LICENSE_SIZE_TO_INSTANCE_SIZE}. Two literal maps
 * would drift the moment one tier is repriced.
 *
 * **This is presentation, not enforcement.** The API is what refuses the write; these
 * caps only decide when the UI disables "Add" and what the counter reads. Until the
 * backend half of CTX-484 lands, the API still resolves caps by exact instance type, so
 * a BYOL reflector or an `m6i.xlarge` will be refused with a 400 even though the UI here
 * allows it.
 */

/** Instance sizes we know caps for. Sizes above `xlarge` collapse onto `xlarge`. */
export type InstanceSize = "micro" | "small" | "medium" | "large" | "xlarge";

/** BYOL license sizes, as sold. There is no XL license. */
export type LicenseSize = "s" | "m" | "l";

export interface InstanceLimitEntry {
  tier: string;
  bgp_sessions: number;
  cpr_limit: number;
}

/**
 * Bare instance size → tier + caps. Mirrors the backend's
 * `utils/reflector-instance-limits.json`, which CTX-484 rekeys from full instance types
 * (`t3.small`) to bare sizes so a new family (m6i, c6g, r6i…) needs no map edit.
 *
 * `xlarge` at 100/100 continues the doubling of the tiers below it. It is **not yet
 * validated with product** — the value the lost prototype had chosen, kept so the tier
 * exists rather than silently falling back to 10.
 */
export const INSTANCE_SIZE_LIMITS: Record<InstanceSize, InstanceLimitEntry> = {
  micro: { tier: "free", bgp_sessions: 2, cpr_limit: 2 },
  small: { tier: "small", bgp_sessions: 10, cpr_limit: 10 },
  medium: { tier: "medium", bgp_sessions: 25, cpr_limit: 25 },
  large: { tier: "large", bgp_sessions: 50, cpr_limit: 50 },
  xlarge: { tier: "xlarge", bgp_sessions: 100, cpr_limit: 100 },
};

/** The backend's `instance_limits.default`: an unrecognised size is treated as Small. */
export const DEFAULT_INSTANCE_LIMITS: InstanceLimitEntry = INSTANCE_SIZE_LIMITS.small;

/**
 * BYOL license size → the instance size whose caps it mirrors. The indirection is the
 * point: repricing Medium once changes both paths.
 */
export const LICENSE_SIZE_TO_INSTANCE_SIZE: Record<LicenseSize, InstanceSize> = {
  s: "small",
  m: "medium",
  l: "large",
};

/** API shape: `reflectorInstance.instanceLimits` on the bgp-config response. */
export interface ApiInstanceLimits {
  tier?: string;
  bgp_sessions?: number | string;
  cpr_limit?: number | string;
  /**
   * What the backend matched on: a license size (`s` / `m` / `l`) when `source` is
   * `license`, otherwise an instance size or full instance type.
   */
  matchedKey?: string;
  /** `license` when the reflector is BYOL; `map` / `default` for the PAYG size path. */
  source?: string;
}

/** Which rule produced the caps, so a surface can word its message honestly. */
export type InstanceCapSource = "license" | "instance" | "api" | "default";

export interface ResolvedInstanceCaps {
  bgpSessions: number;
  cprLimit: number;
  tier: string;
  source: InstanceCapSource;
}

/** Accepts the letter the licenses are sold as and the spelled-out size, either case. */
const LICENSE_SIZE_ALIASES: Record<string, LicenseSize> = {
  s: "s",
  m: "m",
  l: "l",
  small: "s",
  medium: "m",
  large: "l",
};

/**
 * Own-property lookup. A plain index would resolve inherited members, so a `matchedKey`
 * of `"constructor"` would otherwise pass the guard and yield a non-entry.
 */
function ownEntry<T>(map: Record<string, T>, key: string): T | null {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : null;
}

function readTrimmed(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === "string" && value.trim() !== "") {
      return value.trim();
    }
  }
  return "";
}

function toNum(v: unknown): number | undefined {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/** A cap the UI can count against: numeric and not negative. */
function usableCap(v: unknown): number | undefined {
  const n = toNum(v);
  return n !== undefined && n >= 0 ? n : undefined;
}

/**
 * Instance size out of whatever the backend matched on: `t3.medium` → `medium`,
 * `m6i.2xlarge` → `xlarge`, a bare `xlarge` → `xlarge`.
 *
 * Everything at or above `xlarge` collapses onto `xlarge` because that is the largest
 * tier priced — a `4xlarge` is not entitled to more than an `xlarge` today, and guessing
 * a multiple would invent a cap the backend will not honour.
 */
export function instanceSizeFromKey(key: string): InstanceSize | null {
  const bare = key.trim().toLowerCase().split(".").pop() ?? "";
  const size = bare.replace(/^\d+x/, "x");
  return ownEntry(INSTANCE_SIZE_LIMITS, size) ? (size as InstanceSize) : null;
}

/** License size out of the backend's `matchedKey`, or null when it is not one. */
export function licenseSizeFromKey(key: string): LicenseSize | null {
  return ownEntry(LICENSE_SIZE_ALIASES, key.trim().toLowerCase());
}

function entryFromTierName(tier: string): InstanceLimitEntry | null {
  const wanted = tier.trim().toLowerCase();
  if (!wanted) return null;
  return (
    Object.values(INSTANCE_SIZE_LIMITS).find((e) => e.tier.toLowerCase() === wanted) ?? null
  );
}

/**
 * Resolves both caps from `reflectorInstance.instanceLimits`.
 *
 * Order is the contract, most specific first: the BYOL license size, then the instance
 * size, then the tier name, then whatever numbers the API sent, then Small. It resolves
 * the caps together rather than once per field so a surface cannot end up quoting a
 * license-derived neighbour cap next to an instance-derived rule cap.
 *
 * Falls back rather than throwing at every step — an unreadable payload must not black
 * out the page, and the API is the real gate anyway.
 */
export function resolveInstanceCaps(stored: unknown): ResolvedInstanceCaps {
  const fallback: ResolvedInstanceCaps = {
    bgpSessions: DEFAULT_INSTANCE_LIMITS.bgp_sessions,
    cprLimit: DEFAULT_INSTANCE_LIMITS.cpr_limit,
    tier: DEFAULT_INSTANCE_LIMITS.tier,
    source: "default",
  };

  if (!stored || typeof stored !== "object" || Array.isArray(stored)) return fallback;
  const o = stored as Record<string, unknown>;

  const matchedKey = readTrimmed(o, "matchedKey", "matched_key");
  const isLicensed = readTrimmed(o, "source").toLowerCase() === "license";

  // BYOL: the license size decides, and the instance size is not consulted at all.
  if (isLicensed) {
    const licenseSize = licenseSizeFromKey(matchedKey);
    if (licenseSize) {
      const entry = INSTANCE_SIZE_LIMITS[LICENSE_SIZE_TO_INSTANCE_SIZE[licenseSize]];
      return {
        bgpSessions: entry.bgp_sessions,
        cprLimit: entry.cpr_limit,
        tier: entry.tier,
        source: "license",
      };
    }
    // A licensed reflector whose size we cannot read (e.g. a license predating the
    // size field) deliberately keeps going: the API's own numbers below are a better
    // answer than pinning it to Small.
  }

  const instanceSize = isLicensed ? null : instanceSizeFromKey(matchedKey);
  if (instanceSize) {
    const entry = INSTANCE_SIZE_LIMITS[instanceSize];
    return {
      bgpSessions: entry.bgp_sessions,
      cprLimit: entry.cpr_limit,
      tier: entry.tier,
      source: "instance",
    };
  }

  const byTier = isLicensed ? null : entryFromTierName(readTrimmed(o, "tier", "Tier"));
  if (byTier) {
    return {
      bgpSessions: byTier.bgp_sessions,
      cprLimit: byTier.cpr_limit,
      tier: byTier.tier,
      source: "instance",
    };
  }

  // Per-field, because the API may carry only one of the two caps.
  const apiBgp = usableCap(o.bgp_sessions ?? o.bgpSessions);
  const apiCpr = usableCap(o.cpr_limit ?? o.cprLimit);
  if (apiBgp !== undefined || apiCpr !== undefined) {
    return {
      bgpSessions: apiBgp ?? DEFAULT_INSTANCE_LIMITS.bgp_sessions,
      cprLimit: apiCpr ?? DEFAULT_INSTANCE_LIMITS.cpr_limit,
      tier: readTrimmed(o, "tier", "Tier") || DEFAULT_INSTANCE_LIMITS.tier,
      source: isLicensed ? "license" : "api",
    };
  }

  return fallback;
}

/**
 * Max BGP neighbors for the reflector. See {@link resolveInstanceCaps} for the order.
 */
export function getBgpNeighborCapFromStoredLimits(stored: unknown): number {
  return resolveInstanceCaps(stored).bgpSessions;
}

/**
 * Max cloud propagation rules for the reflector. See {@link resolveInstanceCaps}.
 */
export function getCprCapFromStoredLimits(stored: unknown): number {
  return resolveInstanceCaps(stored).cprLimit;
}

/**
 * How to name the thing that set the cap, for user-facing copy. A BYOL customer is
 * capped by what they bought, so telling them it is "for this instance tier" sends them
 * to resize a machine that will not change the limit.
 */
export function describeCapSource(source: InstanceCapSource): string {
  return source === "license" ? "for this license" : "for this instance tier";
}
